import cn from 'clsx'
import React, { Dispatch, FC, SetStateAction } from 'react'
import s from './VariantRectangle.module.css'
import type {
  Product,
  ProductOption,
  ProductOptionValues,
  ProductVariant,
} from '@commerce/types/product'
import { getFirstOpSize, getFirstOpTCW, getValidOptionsForMetal } from './helpers'

export interface VariantRectangleProps {
  className?: string
  setVariantSettings: Dispatch<
    SetStateAction<{
      metal: string
      diam_type: string
      tcw: string
      size: string
    }>
  >
  variants: string[]
  variantSettings: {
    metal: string
    diam_type: string
    tcw: string
    size: string
  }
  product: Product
  optionType: string
  productType?: string
  currentFilters: string[]
}

const VariantRectangle: FC<VariantRectangleProps> = (props) => {
  const {
    className,
    setVariantSettings,
    variantSettings,
    product,
    productType,
    currentFilters,
    variants,
  } = props
  const rootClassName = cn(s.root, className)

  const selectFirstOpt = (typeSelected: string) => {
    // Get the first available options according to the selected diamond type
    const firstOpTCW = getFirstOpTCW(
      product,
      variantSettings,
      typeSelected,
      currentFilters
    );
    const firstOptSize = getFirstOpSize(
      product,
      variantSettings,
      typeSelected,
      currentFilters
    );

    // Get valid TCW (Total Carat Weight) options for the selected metal
    const validTcwOptions = getValidOptionsForMetal(
      variantSettings.metal, // We use the current metal
      typeSelected, // We use the selected diamond type
      product.variants,
      'total carat weight'
    );

    // Get valid Size options for the selected metal and diamond type
    const validSizeOptions = getValidOptionsForMetal(
      variantSettings.metal, // We use the current metal
      typeSelected, // We use the selected diamond type
      product.variants,
      'size'
    );

    // Check if the current TCW is valid for the selected diamond type
    const isValidCurrentTCW = validTcwOptions.includes(variantSettings.tcw);

    const validCurrentSize = validSizeOptions.includes(variantSettings.size);

    setVariantSettings((prev) => ({
      ...prev,
      diam_type: typeSelected,
      tcw: isValidCurrentTCW ? variantSettings.tcw : firstOpTCW,
      size: validCurrentSize ? prev.size : firstOptSize,
    }));
  };

  return (
    <div className={rootClassName}>
      <div className={s.header}>
        <span className={s.type}>Diamond Type:</span>
        <span className={s.title}>
          {variantSettings.diam_type === '' ? '-' : variantSettings.diam_type}
        </span>
      </div>
      <div className={s.container}>
        {variants.some((v) => v === 'Natural') && (
          <div
            className={cn(s.label, {
              '!border-accent-9': variantSettings.diam_type === 'Natural',
            })}
            onClick={() => {
              setVariantSettings((prev) => ({ ...prev, diam_type: 'Natural' }))
              selectFirstOpt('Natural')
            }}
          >
            <span>Natural</span>
          </div>
        )}
        {variants.some((v) => v === 'Lab') && (
          <div
            className={cn(s.label, {
              '!border-accent-9': variantSettings.diam_type === 'Lab',
            })}
            onClick={() => {
              setVariantSettings((prev) => ({ ...prev, diam_type: 'Lab' }))
              selectFirstOpt('Lab')
            }}
          >
            <span>Lab</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default React.memo(VariantRectangle)
